/**
 * Auth0 Api Module
 * @see https://auth0.com/
 * @see https://www.npmjs.com/package/auth0-js
 */
import * as Auth0 from 'auth0-js'
import { env } from '@/libs/env'

type AuthOptions = {
  domain: string
  clientID: string
  responseType?: string
  responseMode?: string
  redirectUri?: string
  scope?: string
  audience?: string
  leeway?: number
  plugins?: any[]
  _disableDeprecationWarnings?: boolean
  _sendTelemetry?: boolean
  _telemetryInfo?: any
  __tryLocalStorageFirst?: boolean
}

/**
 * サインイン
 * 成功した場合、指定されたリダイレクト先に遷移します
 * @param email
 * @param password
 * @return Promise
 */
export const signin = (email, password) => {
  return new Promise((resolve, reject) => {
    const { domain, clientID, responseType, redirectUri } = _buildWebAuthParam()
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
      responseType,
      redirectUri,
    })
    webAuth.login(
      {
        realm: env('AUTH_DBNAME'),
        email: email,
        password: password,
      },
      (error, result) => {
        if (!error) {
          resolve(result)
        } else {
          console.error(error)
          // reject(translate('メールアドレスもしくはパスワードが間違っています'))
          reject('メールアドレスもしくはパスワードが間違っています')
        }
      },
    )
  })
}

/**
 * QRチェックイン用サインイン
 * 成功した場合、指定されたリダイレクト先に遷移します
 * @param email
 * @param password
 * @return Promise
 */
export const qrSignin = (email, password) => {
  return new Promise((resolve, reject) => {
    const { domain, clientID, responseType, redirectUri } = _buildWebAuthParamForQrCheckin()
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
      responseType,
      redirectUri,
    })
    webAuth.login(
      {
        realm: env('AUTH_DBNAME'),
        email: email,
        password: password,
      },
      (error, result) => {
        if (!error) {
          resolve(result)
        } else {
          console.error(error)
          // reject(translate('メールアドレスもしくはパスワードが間違っています'))
          reject('メールアドレスもしくはパスワードが間違っています')
        }
      },
    )
  })
}

/**
 * サインアウト
 * @return Promise
 */
export const signout = () => {
  return new Promise(() => {
    localStorage.removeItem('idToken')
    const { domain, clientID, redirectUri } = _buildWebAuthParam()
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
    })
    webAuth.logout({ returnTo: redirectUri })
  })
}

/**
 * QRチェックイン用サインアウト
 * @return Promise
 */
export const qrSignout = () => {
  return new Promise(() => {
    localStorage.removeItem('idToken')
    const { domain, clientID, redirectUri } = _buildWebAuthParamForQrCheckin()
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
    })
    webAuth.logout({ returnTo: redirectUri })
  })
}

/**
 * トークン解析
 * @return Promise
 */
export const parseToken = (): Promise<Auth0.Auth0DecodedHash> => {
  return new Promise((resolve, reject) => {
    const { domain, clientID } = _buildWebAuthParam()
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
    })
    webAuth.parseHash((error, result) => {
      if (result) {
        resolve(result)
      } else {
        console.error(error)
        reject(error)
      }
    })
  })
}

/**
 * トークン更新
 * @return Promise
 */
export const refreshToken = () => {
  return new Promise((resolve, reject) => {
    const { domain, clientID, responseType, redirectUri } = _buildWebAuthParam()
    console.log('refreshToken: domain', domain)
    console.log('refreshToken: clientID', clientID)
    console.log('refreshToken: responseType', responseType)
    console.log('refreshToken: redirectUri', redirectUri)
    const webAuth: Auth0.WebAuth = new Auth0.WebAuth({
      domain,
      clientID,
      responseType,
      redirectUri,
    })
    webAuth.checkSession({}, (error, result) => {
      if (!error) {
        resolve(result)
      } else {
        console.error(error)
        reject(error)
      }
    })
  })
}

/**
 * パラメータ構築
 * @private
 */
const _buildWebAuthParam = (): AuthOptions => {
  const { origin } = window.location
  return {
    domain: env('AUTH_DOMAIN') as string,
    clientID: env('AUTH_CLIENT_ID') as string,
    responseType: 'token id_token',
    redirectUri: `${origin}/`,
  }
}

/**
 * パラメータ構築(QRチェックイン用)
 * @private
 */
const _buildWebAuthParamForQrCheckin = (): AuthOptions => {
  const { origin } = window.location
  return {
    domain: env('AUTH_DOMAIN') as string,
    clientID: env('AUTH_CLIENT_ID') as string,
    responseType: 'token id_token',
    redirectUri: `${origin}/qr-checkin`,
  }
}
